import React, { useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import {
	LicenseDesignation,
	LicenseOption,
	LicenseType,
	Severity,
} from "node-library/dist/constants";
import { NeutralColor, PrimaryColor } from "node-library/dist/colors";
import { Card } from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import {
	FrontendClient,
	FrontendOrganization,
	FrontendOrganizationLicense,
} from "node-library/dist/portal";
import { KebabDropdownButton } from "../../../common-components/button";
import {
	DeleteIcon,
	EditIcon,
	LicenseIcon,
	UserIcon,
} from "../../../common-components/svgIcons";

import { sendPOSTRequest } from "../../../requestHelper/sendRequest";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import {
	RedErrorIcon,
	RedTrashIcon,
} from "../../../common-components/toast/errorIcons";
import AddOrEditClientModal from "./modal/addOrEditClientModal";

interface Props {
	clients: FrontendClient[];
	setClients: React.Dispatch<React.SetStateAction<FrontendClient[]>>;
	organization: FrontendOrganization;
}

export default function ClientInfoSection(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	//license info
	const { clients, setClients, organization } = props;

	const [addEditClientModalOpen, setAddEditClientModalOpen] =
		useState<boolean>(false);

	const [loading, setLoading] = useState<boolean>(false);

	const [proceedAction, setProceedAction] = useState<() => void>(
		() => () => {}
	);
	const [selectedClient, setSelectedClient] = useState<
		FrontendClient | undefined
	>();

	const addClient = (newClient: FrontendClient) => {
		setClients((prevClients) => [...prevClients, newClient]);
		setSelectedClient(undefined);
	};

	const updateClient = (updatedClient: FrontendClient) => {
		setClients((prevClients) =>
			prevClients.map((client) =>
				client.clientId === updatedClient.clientId
					? { ...client, ...updatedClient }
					: client
			)
		);
		setSelectedClient(undefined);
	};

	const clientOptionCards: Array<React.ReactNode> = clients.map(
		(client, index) => {
			return (
				<Card
					className={`card-border-radius-10p card-medium-padding`}
					tabIndex={0}
					key={client.clientId}
					id={`client-option-card-${index}`}
					sx={{ backgroundColor: "#F3F3F5", height: "160px" }}
				>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							justifyContent: "space-between",
							height: "100%",
						}}
					>
						<div>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									gap: "10px",
								}}
							>
								<div
									style={{
										display: "flex",
										flexDirection: "column",
										gap: "5px",
										maxWidth: "80%",
									}}
								>
									<div
										className="typography-caption"
										style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
									>
										{translation("ClientName")}
									</div>
									<div className="typography-heading-03 overflow-ellipsis-text">
										{`${client.firstName || ""} ${
											client.lastName || ""
										}`.trim()}
									</div>
								</div>
								<KebabDropdownButton
									menuOptions={[
										{
											key: `edit-client-${client.clientId}`,
											onClick: () => {
												setSelectedClient(client);
												setAddEditClientModalOpen(true);
											},
											children: (
												<div className="icon-dropdown-menu-item">
													<EditIcon
														strokeColor={NeutralColor.NEUTRAL_GREY_950}
													/>
													{translation("Edit")}
												</div>
											),
										},
									]}
									id={`client-more-action-menu-${client.clientId}`}
									onClick={(e) => {}}
								></KebabDropdownButton>
							</div>
						</div>
						<div className="typography-caption profile-license-designation-type-row">
							<div className="overflow-ellipsis-text">
								<div
									className="typography-caption"
									style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
								>
									{translation("Email")}
								</div>
								<div className="typography-body-02">
									{client.email || translation("None")}
								</div>
							</div>

							<div className="overflow-ellipsis-text">
								<div
									className="typography-caption"
									style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
								>
									{translation("PhoneNumber")}
								</div>
								<div className="typography-body-02">
									{client.phoneNumber || translation("None")}
								</div>
							</div>
						</div>
					</div>
				</Card>
			);
		}
	);

	const newClientCard: React.ReactNode = (
		<Card
			className={`card-border-radius-10p card-medium-padding`}
			tabIndex={0}
			sx={{
				border: `1px solid ${PrimaryColor.PRIMARY_LV5}`,
				height: "160px",
				color: PrimaryColor.PRIMARY_600,
				cursor: "pointer",
				transition: "all 0.3s ease", // Smooth transition
				"&:hover": {
					backgroundColor: PrimaryColor.PRIMARY_50, // Color on hover
				},
				"&:active": {
					backgroundColor: PrimaryColor.PRIMARY_100, // Color when pressed
				},
			}}
			onClick={() => {
				setSelectedClient(undefined);
				setAddEditClientModalOpen(true);
			}}
		>
			<div
				style={{
					height: "100%",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
				className="typography-body-01"
			>
				<AddIcon></AddIcon>
				{translation("NewClient")}
			</div>
		</Card>
	);

	const addEditClientModal: React.ReactNode = (
		<AddOrEditClientModal
			open={addEditClientModalOpen}
			setOpen={setAddEditClientModalOpen}
			addOrEditClient={selectedClient ? updateClient : addClient}
			organization={organization}
			client={selectedClient}
		></AddOrEditClientModal>
	);

	const clientInformation: React.ReactNode = (
		<Card className="common-card card-normal-padding">
			<div>
				<div className="profile-information-container">
					<div className="profile-icon-header-row typography-heading-03">
						<UserIcon></UserIcon>
						{translation("ClientInformation")}
					</div>
					<div
						className="typography-caption"
						style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
					>
						{translation("ClientInformationDescription")}
					</div>
				</div>

				<div className="profile-license-card-grid">
					{clientOptionCards} {newClientCard}
				</div>
				{addEditClientModal}
			</div>
		</Card>
	);

	return (
		<>
			{clientInformation}
			{/* {
				<DeleteLicenseConfirmModal
					open={deleteLicenseModalOpen}
					setOpen={setDeleteLicenseModalOpen}
					proceedAction={proceedAction}
				/>
			} */}
		</>
	);
}
